import { DEFAULT_NAMESPACE, ALLOWED_TENANTS, getConfig } from './config';
import {
  type LoggerConfig,
  type NamespaceConfig,
  StageVariants,
  type NamespacedLogger,
  type ConsoleFunction,
  type DisabledConsoleMethod,
  type ConsoleMethods,
  type DatadogConfig,
  type DatadogLoggerArgs,
} from './types';
import { consoleTransformer, hashCode, intToRGB, isBrowser } from './utils';
import { version } from '../package.json' with { type: 'json' };
import type { Logger } from '@datadog/browser-logs';
import type { Logger as WinstonLogger } from 'winston';
import { createLogger } from '@seamless-logger/lib';
/**
 * SeamlessLogger
 *
 * Provides access to the browser's debugging console. The specifics of how it works varies from browser to browser,
 * but there is a de facto set of features that are typically provided. This class is an extension from Web Console API.
 *
 * Be aware the not every single console API method is enabled in the SeamlessLogger class (eg. console.dir(), console.assert())
 *
 * The SeamlessLogger instance should be used as a singleton. It can contain multiple namespaces and it will append this namespace
 * to the logged messages.
 *
 * @export
 * @class SeamlessLogger
 */
export class SeamlessLogger {
  /**
   * version
   * @type {string}
   * @memberof SeamlessLogger
   */
  public version: string;

  /**
   * A map of namespaces and their configuration like logging color and debug mode enabled or not
   * @ignore
   * @private
   * @type {Map<string, NamespaceConfig>}
   * @memberof SeamlessLogger
   */
  private namespaceMap: Map<string, NamespaceConfig>;

  /**
   * Tells to the SeamlessLogger if is PROD environment
   * @ignore
   * @private
   * @type {boolean}
   * @memberof SeamlessLogger
   */
  private isProdEnvironment: boolean;

  /**
   * Configuration object
   * @ignore
   * @private
   * @type {LoggerConfig}
   * @memberof SeamlessLogger
   */
  private config: LoggerConfig;

  /** @ignore */
  constructor() {
    this.namespaceMap = new Map();
    this.isProdEnvironment = this.checkIfProdEnvironment();
    this.config = getConfig();
    this.version = version;
  }

  /**
   * A map of namespaces to their Datadog/Winston logger instances. Each namespace
   * that opts in to Datadog gets its own logger stored here so that log calls are
   * always attributed to the namespace that emitted them (rather than to whichever
   * namespace registered a Datadog logger last).
   * @ignore
   * @private
   */
  private loggerMap: Map<string, Logger | WinstonLogger> = new Map();

  /**
   * Service name for datadog
   * @ignore
   * @private
   * @type {string}
   * @memberof SeamlessLogger
   */
  private static datadogService: string = 'seamless-logger';

  static disabledMethod(): void {
    // intentionally left blank
  }

  /**
   * Checks if the SeamlessLogger is running in a PROD environment.
   * @returns {boolean} if not used in SSR context, gets the environment from the window object. If used in SSR context, returns true.
   */
  private checkIfProdEnvironment(): boolean {
    if (isBrowser()) {
      const globalContext = (window as any).__GLOBAL_CONTEXT__?.get?.();
      // TODO: refine the actual variable names when it exists and remove the fallback to true
      if (globalContext) {
        // Check for both stageVariant and runMode, return true if either is missing
        if (globalContext.stageVariant && globalContext.runMode) {
          return globalContext.stageVariant === StageVariants.prod && globalContext.runMode === 'PUBLISH';
        }
        return true;
      }

      const aemNamespace = (window as any).aemNamespace;

      return !!(
        aemNamespace &&
        aemNamespace.pageEnvironmentVariables &&
        aemNamespace.pageEnvironmentVariables.stageVariant === StageVariants.prod &&
        aemNamespace.pageEnvironmentVariables.runMode === 'PUBLISH'
      );
    }

    return true;
  }

  /**
   * Checks if the current tenant is an allowed tenant
   * @returns {boolean}
   */
  private checkIfAllowedTenant(tenant: string): boolean {
    return ALLOWED_TENANTS.includes(tenant);
  }

  /**
   * Adds the provided namespace string to the instance namespace array and assigns a color to it
   *
   * @ignore
   * @memberof SeamlessLogger
   * @param {string} namespace Namespace to be added to the namespace array
   */
  public addNamespace(namespace: string, datadogConfig?: DatadogConfig): void {
    const isAllowedTenant = this.checkIfAllowedTenant(datadogConfig?.tenant || '');
    if (!this.namespaceMap.has(namespace)) {
      const color = intToRGB(hashCode(namespace));

      // Datadog cannot be enabled for the default namespace
      if (datadogConfig && namespace !== DEFAULT_NAMESPACE) {
        const loggerContext = {
          tenant: datadogConfig.tenant,
          'logger-version': this.version,
          namespace,
          ...datadogConfig.configs,
        };

        const namespaceLogger = createLogger({
          loggerContext,
          level: isAllowedTenant && datadogConfig.level ? datadogConfig.level : 'warn',
          namespace,
          version: this.version,
          datadogService: SeamlessLogger.datadogService,
        });

        if (namespaceLogger) {
          this.loggerMap.set(namespace, namespaceLogger);
        }
      }

      this.namespaceMap.set(namespace, {
        color,
        isDebugEnabled: this.isDebugEnabledForNamespace(namespace),
        hasAllowedDatadogTenant: isAllowedTenant,
      });

      const instance = this.getNamespacedInstance(namespace);
      if (datadogConfig && namespace === DEFAULT_NAMESPACE) {
        instance.warn('Datadog cannot be enabled for the default namespace');
      }
    }
  }

  /**
   * returns the Logger instance methods with the correct namespace set.
   *
   * If no namespace is provided the default namespaced instance is provided.
   *
   * @ignore
   * @memberof SeamlessLogger
   * @param {string} namespace namespace name
   * @return {*}  {SeamlessLogger} The logger instance correctly namespaced
   */
  public getNamespacedInstance(namespace?: string): NamespacedLogger {
    let selectedNamespace: string;

    if (namespace && this.namespaceMap.has(namespace)) {
      selectedNamespace = namespace;
    } else {
      if (!this.namespaceMap.has(DEFAULT_NAMESPACE)) {
        this.addNamespace(DEFAULT_NAMESPACE);
      }
      selectedNamespace = DEFAULT_NAMESPACE;
    }

    const permissions = this.loggerPermissionHandler(selectedNamespace);
    return permissions;
  }

  /**
   * Reads from the configuration object if the namespace provided must have the debug mode enabled
   * @param namespace
   * @returns a boolean stating if this namespace must enable the debug mode or not
   */
  private isDebugEnabledForNamespace(namespace: string): boolean {
    return this.config.debugNamespaces.includes(namespace);
  }

  /**
   * Checks if SeamlessLogger is not running in Prod Env or the namespace has the debug mode enable
   * so the logger allows for the function to log or not
   * @param namespace logger namespace that's invoking the call
   */
  private loggerPermissionHandler(namespace: string): any {
    const namespaceConfig = this.namespaceMap.get(namespace);

    const getAllowedMethods = (isEnabled: boolean) => {
      const additionalMethods: ConsoleMethods[] = ['log', 'debug'];
      const defaultAllowedMethods = {
        error: this.error(namespace),
        warn: this.warn(namespace),
        clear: this.clear,
        version: this.version,
      };

      const allowedMethods = additionalMethods.reduce(
        (methods, method) => {
          methods[method] = isEnabled
            ? this.bindConsole(method, namespace, namespaceConfig ? namespaceConfig.color : '')
            : SeamlessLogger.disabledMethod;
          return methods;
        },
        {} as Record<string, any>,
      );

      /**
       * If an allowed tenant is using datadog we provide the logger
       * to the logger instance so the user can change the logger context if needed
       */
      const datadogMethod: Pick<NamespacedLogger, 'logger'> = {};
      const namespaceLogger = this.loggerMap.get(namespace);
      if (namespaceConfig?.hasAllowedDatadogTenant && namespaceLogger) {
        datadogMethod.logger = namespaceLogger;
      }

      return {
        ...datadogMethod,
        ...defaultAllowedMethods,
        ...allowedMethods,
        table: isEnabled
          ? (...args: any[]) => this.bindConsole('table', namespace, namespaceConfig ? namespaceConfig.color : '', args)
          : SeamlessLogger.disabledMethod,
      };
    };

    /**
     * when localStorage is filled with logger namespaces, only those are allowed to log messages
     * the other logger instances can only log errors
     * if localStorage is empty, we check if we are in a non Prod env to log, if not only errors are allowed
     */
    if (this.config.debugNamespaces.length > 0) {
      if (this.config.debugNamespaces.indexOf(namespace) != -1) {
        return getAllowedMethods(true);
      }
    } else if (!this.isProdEnvironment) {
      return getAllowedMethods(true);
    }

    return getAllowedMethods(false);
  }

  /**
   * Function similar to loggerPermissionHandler and should be called only by it
   * allows for permitted calls to be binded and executed
   */
  private bindConsole(
    consoleMethod: ConsoleMethods,
    namespace: string,
    color: string,
    args?: any,
  ): ConsoleFunction | DisabledConsoleMethod {
    let rvalue: ConsoleFunction | DisabledConsoleMethod;
    const namespaceConfig = this.namespaceMap.get(namespace);
    const bindConsole = consoleMethod !== 'table' && consoleTransformer(consoleMethod, namespace, color);
    const logger = this.loggerMap.get(namespace);

    switch (consoleMethod) {
      case 'table':
        console.table(...args);
        rvalue = SeamlessLogger.disabledMethod;
        break;
      case 'log':
      case 'debug':
        if (namespaceConfig?.hasAllowedDatadogTenant && logger) {
          rvalue = (...logArgs: unknown[]) => {
            logger[consoleMethod === 'log' ? 'info' : consoleMethod](JSON.stringify(logArgs).replace(/,/g, ' '));
            (bindConsole as ConsoleFunction)(...logArgs);
          };
        } else {
          rvalue = bindConsole as ConsoleFunction;
        }
        break;
      default:
        rvalue = SeamlessLogger.disabledMethod;
    }

    return rvalue;
  }

  /**
   * prefix and styles the default console api method with the desired namespace.
   *
   * Error:
   * Outputs an error message to the Web Console. (see https://developer.mozilla.org/en-US/docs/Web/API/Console/error)
   *
   * @memberof SeamlessLogger
   * @param {string} namespace the namespace name to prefix the logs
   * @param {unknown[]} args all the arguments that the user wishes to be logged [1-*]
   * @returns {*} {ConsoleFuncton | void} default console api method with the correct namespace prefix
   */
  private error(namespace: string): ConsoleFunction {
    const namespaceConfig = this.namespaceMap.get(namespace);
    const bindConsole = consoleTransformer('error', namespace, namespaceConfig?.color);
    // Send error to datadog if enabled
    const logger = this.loggerMap.get(namespace);
    if (logger) {
      return (...args: unknown[]) => {
        const result = this.loggerArgsValidator(args);

        logger.error(result.message, result.messageContext, result.error);
        if (isBrowser()) {
          bindConsole(...args);
        }
      };
    }

    return bindConsole;
  }

  /**
   * prefix and styles the default console api method (warn) with the desired namespace.
   *
   * Warn:
   * Outputs a warning message to Datadog (always) and to the Web Console (only if debug enabled).
   * This allows warns to be sent to Datadog for monitoring while keeping console clean in production.
   *
   * @memberof SeamlessLogger
   * @param {string} namespace the namespace name to prefix the logs
   * @returns {*} {ConsoleFunction} method that handles warn logging
   */
  private warn(namespace: string): ConsoleFunction {
    const namespaceConfig = this.namespaceMap.get(namespace);
    const bindConsole = consoleTransformer('warn', namespace, namespaceConfig?.color);

    // Send warn to datadog if enabled (always, regardless of tenant status)
    const logger = this.loggerMap.get(namespace);

    if (logger) {
      return (...args: unknown[]) => {
        // Always send to Datadog
        const result = this.loggerArgsValidator(args);
        logger.warn(result.message, result.messageContext, result.error);

        // Only log to console if debug is enabled for this namespace or not in production
        const shouldLogToConsole = namespaceConfig?.isDebugEnabled || !this.isProdEnvironment;
        if (shouldLogToConsole && isBrowser()) {
          bindConsole(...args);
        }
      };
    }

    // If no Datadog, fall back to normal console behavior (respects debug settings)
    return namespaceConfig?.isDebugEnabled || !this.isProdEnvironment ? bindConsole : SeamlessLogger.disabledMethod;
  }

  /**
   * validates the args parameter and returns its deconstructed object for the logger.error and warn methods
   * @memberof SeamlessLogger
   * @param {unknown[]} args all the arguments that the user wishes to be logged [1-*]
   * @returns {DatadogLoggerArgs} object with the properties necessary for the logger.error method
   */
  private loggerArgsValidator(args: unknown[]): DatadogLoggerArgs {
    const result: DatadogLoggerArgs = {};

    if (!args.length) {
      console.warn('Datadog Logger - at least one argument should be provided');
      return result;
    }

    const [message, messageContext, error] = args;

    if (messageContext !== null && typeof messageContext === 'object') {
      result.messageContext = messageContext;
    } else if (messageContext !== undefined) {
      console.warn('Datadog Logger - second argument must be a non-null object');
    }

    if (error !== null && error instanceof Error) {
      result.error = error;
    } else if (error !== undefined) {
      console.warn('Datadog Logger - third argument must be a non-null instance of Error');
    }

    if (result.messageContext) {
      result.message = message;
    } else {
      result.message = args.join().replace(/,/g, ' ');
    }

    return result;
  }

  /**
   * prefix and styles the default console api method with the desired namespace.
   *
   * CLear:
   * Clears the console if the environment allows it. (see https://developer.mozilla.org/en-US/docs/Web/API/Console/clear)
   *
   * @memberof SeamlessLogger
   * @param {string} namespace the namespace name to prefix the logs
   * @param {unknown[]} args all the arguments that the user wishes to be logged [1-*]
   * @returns {*} {ConsoleFuncton | void} default console api method with the correct namespace prefix
   */
  private clear(): void {
    console.clear();
  }
}
